import React, { useMemo, useState } from "react";

import { SCORE_TYPE_LABELS } from "./BuilderUtils";
import { authoredDecisionIds } from "./measurePool";
import { chartScoreIdsOf } from "../interaction_pages/commandControlCharts";

/**
 * SYNTHÈSE DES IMPACTS — les mesures en colonnes, les scores en lignes.
 *
 * CE QU'ELLE RÉSOUT. L'équilibrage d'une page de décisions est une question de
 * COMPARAISON : ce qui coûte cher rapporte-t-il assez, deux mesures pèsent-elles
 * pareil, un score est-il oublié de tout le monde. Or les impacts étaient rangés
 * un par décision, dans un formulaire par décision : pour comparer, il fallait
 * ouvrir neuf fiches à la suite et tenir les chiffres de tête. Le tableau les met
 * tous sous les yeux, et les rend modifiables là où on les compare.
 *
 * POURQUOI LES CHIFFRES SONT AUSSI DES COULEURS. Un tableau de nombres se lit
 * ligne à ligne ; l'équilibrage se voit d'un coup d'œil. L'intensité dit
 * l'ampleur relative AU SEIN D'UNE LIGNE — comparer un coût en euros à des
 * points de satisfaction n'a aucun sens, chaque score a son échelle.
 *
 * LES LIGNES SONT GROUPÉES PAR NATURE DE SCORE, parce qu'on ne les équilibre pas
 * de la même façon : un budget se dépense, la satisfaction d'un acteur se
 * négocie, une sous-métrique CBS pèse via son coefficient. Les mélanger dans une
 * liste à plat obligeait à se rappeler, ligne par ligne, ce qu'on regardait.
 *
 * CE QU'ON MODIFIE ICI N'EST PAS LA PAGE, CE SONT LES MESURES. Une décision est
 * partagée entre toutes les pages qui la proposent : changer un impact les
 * change toutes. Le bandeau le dit, et l'enregistrement passe par les mêmes
 * garde-fous que l'éditeur de décision.
 */

/* ─── Les trois natures d'impact ───────────────────────────────────────
   Le moteur ne lit pas un impact de la même façon selon le score visé :

   - SIMPLE   : `score[id].impact` s'ajoute à la valeur du score.
   - COMPOSITE: le score porte `metadata: {standard, current}` — un total
     réparti en postes. L'impact est alors `score[id].metadata`, un delta par
     poste (cf. game_functions.py). Un seul nombre ne peut pas le décrire.
   - PROFIL   : `water_projection` et consorts, dont l'impact est une courbe
     (`metadata.water_evolution`) étalée sur les rounds suivants et dépendante
     du secteur de la décision. Ça ne se règle pas dans une cellule.
*/
export function scoreShape(scoreDef) {
  const metadata = scoreDef?.metadata;
  if (metadata && typeof metadata === "object") {
    if (metadata.standard && metadata.current) return "composite";
    return "profile";
  }
  return "simple";
}

/** Les postes d'un score composite, dans l'ordre du score_dic. */
function partsOf(scoreDef) {
  return Object.keys(scoreDef?.metadata?.current || {});
}

function simpleImpact(decision, scoreId) {
  const entry = decision?.score?.[scoreId];
  if (!entry || entry.impact === undefined || entry.impact === null || entry.impact === "") return null;
  return Number(entry.impact);
}

/** Somme des deltas d'un impact composite — ce que la cellule résume. */
function compositeTotal(decision, scoreId) {
  const parts = decision?.score?.[scoreId]?.metadata;
  if (!parts || typeof parts !== "object") return null;
  const values = Object.values(parts).filter((v) => typeof v === "number");
  if (values.length === 0) return null;
  return values.reduce((sum, v) => sum + v, 0);
}

const TYPE_ORDER = ["metric", "actor", "cbs-submetric", "custom"];

export default function ScoreMatrix({
  page, ctx, decisionsById, onChangeDecision, onEditDecision,
  fullscreen, onToggleFullscreen,
}) {
  const { scoreDic } = ctx;
  const [showAllScores, setShowAllScores] = useState(false);
  const [editing, setEditing] = useState(null); // `${decId}|${scoreId}` — cellule simple
  const [composing, setComposing] = useState(null); // { decId, scoreId } — cellule composite

  // Le réservoir entier pour une page qui pioche : équilibrer des impacts
  // sur un seul tirage ne dirait rien de la page.
  const decisionIds = authoredDecisionIds(page, ctx.config);
  const decisions = decisionIds.map((id) => decisionsById[id]).filter(Boolean);

  // QUELLES LIGNES. Par défaut, les scores qui ont quelque chose à dire sur
  // cette page : ceux qu'au moins une mesure touche, plus ceux que la page
  // affiche (même à zéro — leur absence d'impact est une information). Les
  // autres se demandent, ils ne s'imposent pas : un score_dic d'atelier compte
  // facilement vingt entrées dont deux concernent cette page.
  const rows = useMemo(() => {
    const all = Object.keys(scoreDic || {});
    if (showAllScores) return all;
    const shown = new Set([
      ...(page.content?.display_score_decisions || []).map((d) => d.score_id),
      ...(page.content?.list_additional_indicators || []),
      ...chartScoreIdsOf(page.content),
    ].filter(Boolean));
    const touched = new Set();
    decisions.forEach((dec) => {
      Object.keys(dec.score || {}).forEach((scoreId) => touched.add(scoreId));
    });
    return all.filter((id) => shown.has(id) || touched.has(id));
  }, [scoreDic, showAllScores, page.content, decisions]);

  // Groupées par nature, dans un ordre fixe : on ne veut pas que la structure du
  // tableau change selon l'ordre de saisie du score_dic.
  const groups = useMemo(() => {
    const byType = {};
    rows.forEach((scoreId) => {
      const type = scoreDic?.[scoreId]?.type || "metric";
      (byType[type] = byType[type] || []).push(scoreId);
    });
    return TYPE_ORDER
      .filter((type) => byType[type]?.length)
      .map((type) => ({ type, label: SCORE_TYPE_LABELS[type] || type, scoreIds: byType[type] }));
  }, [rows, scoreDic]);

  // L'échelle est propre à chaque ligne : comparer un coût en euros à des points
  // de satisfaction n'aurait pas de sens.
  const rowScale = useMemo(() => {
    const scale = {};
    rows.forEach((scoreId) => {
      const shape = scoreShape(scoreDic?.[scoreId]);
      const read = shape === "composite" ? compositeTotal : simpleImpact;
      const values = decisions.map((dec) => Math.abs(read(dec, scoreId) ?? 0));
      scale[scoreId] = Math.max(1, ...values);
    });
    return scale;
  }, [rows, decisions, scoreDic]);

  /* ── Écritures ─────────────────────────────────────────────────── */

  // TOUJOURS UN `impact`, MÊME SUR UN COMPOSITE. Le moteur lit `score['impact']`
  // sans garde (game_functions.py) : une entrée qui n'en porterait pas ferait
  // planter la partie au moment où la mesure est prise, en pleine séance.
  const writeEntry = (decId, scoreId, entry) => {
    const decision = decisionsById[decId];
    if (!decision) return;
    const next = { ...decision, score: { ...(decision.score || {}) } };
    if (entry === null) delete next.score[scoreId];
    else next.score[scoreId] = { impact: 0, ...next.score[scoreId], ...entry };
    onChangeDecision(decId, next);
  };

  const setSimpleImpact = (decId, scoreId, raw) => {
    // Retirer la valeur retire l'impact : une entrée à zéro et une absence
    // d'entrée ne se distinguent pas en jeu, autant ne pas laisser traîner des
    // clés vides dans le document.
    if (raw === "" || raw === "-") return writeEntry(decId, scoreId, null);
    writeEntry(decId, scoreId, { impact: Number(raw) });
  };

  const setCompositePart = (decId, scoreId, part, raw) => {
    const decision = decisionsById[decId];
    const current = { ...(decision?.score?.[scoreId]?.metadata || {}) };
    if (raw === "" || raw === "-") delete current[part];
    else current[part] = Number(raw);
    if (Object.keys(current).length === 0) return writeEntry(decId, scoreId, null);
    writeEntry(decId, scoreId, { metadata: current });
  };

  if (decisions.length === 0) {
    return (
      <div className="builder-empty" style={{ padding: 48 }}>
        Aucune mesure sur cette page — il n'y a rien à comparer.
        Ajoutez des décisions dans l'onglet « Contenu ».
      </div>
    );
  }

  const cell = (dec, scoreId, shape) => {
    const key = `${dec._id}|${scoreId}`;
    const def = scoreDic?.[scoreId] || {};

    if (shape === "profile") {
      // Une courbe étalée sur les rounds, dépendante du secteur de la mesure :
      // la résumer dans une case serait mentir, la régler ici serait pire.
      const has = !!dec.score?.[scoreId]?.metadata;
      return (
        <button
          type="button"
          className={`score-matrix-value is-profile${has ? " has-value" : ""}`}
          onClick={() => onEditDecision?.(dec._id)}
          title={has
            ? "Impact en profil (courbe sur plusieurs rounds), se règle dans la fiche de la mesure"
            : "Aucun profil. Se règle dans la fiche de la mesure."}
        >{has ? "∿ profil" : "·"}</button>
      );
    }

    if (shape === "composite") {
      const total = compositeTotal(dec, scoreId);
      const ratio = total === null ? 0 : Math.abs(total) / rowScale[scoreId];
      const tone = total === null ? "none" : total > 0 ? "up" : total < 0 ? "down" : "zero";
      const count = Object.keys(dec.score?.[scoreId]?.metadata || {}).length;
      return (
        <button
          type="button"
          className={`score-matrix-value is-composite is-${tone}`}
          style={total === null ? undefined : { "--cell-strength": ratio.toFixed(3) }}
          onClick={() => setComposing({ decId: dec._id, scoreId })}
          title={`${def.name || scoreId} · ${dec.title || dec._id} — ${count} poste(s) touché(s). Cliquez pour détailler.`}
        >
          {total === null ? "·" : (
            <>
              {total > 0 ? `+${total}` : total}
              <span className="score-matrix-parts">{count} poste{count > 1 ? "s" : ""}</span>
            </>
          )}
        </button>
      );
    }

    const value = simpleImpact(dec, scoreId);
    if (editing === key) {
      return (
        <input
          className="score-matrix-input"
          type="number"
          autoFocus
          defaultValue={value ?? ""}
          onBlur={(e) => { setSimpleImpact(dec._id, scoreId, e.target.value); setEditing(null); }}
          onKeyDown={(e) => {
            if (e.key === "Enter") e.currentTarget.blur();
            if (e.key === "Escape") setEditing(null);
          }}
        />
      );
    }
    const ratio = value === null ? 0 : Math.abs(value) / rowScale[scoreId];
    const tone = value === null ? "none" : value > 0 ? "up" : value < 0 ? "down" : "zero";
    return (
      <button
        type="button"
        className={`score-matrix-value is-${tone}`}
        // L'intensité porte l'ampleur relative dans la ligne ; le signe reste dit
        // par la couleur ET par le chiffre, pour ne pas dépendre de la
        // perception des couleurs.
        style={value === null ? undefined : { "--cell-strength": ratio.toFixed(3) }}
        onClick={() => setEditing(key)}
        title={`${def.name || scoreId} · ${dec.title || dec._id}`}
      >
        {value === null ? "·" : value > 0 ? `+${value}` : value}
      </button>
    );
  };

  return (
    <div className="score-matrix">
      <div className="score-matrix-toolbar">
        <div className="score-matrix-notice">
          Les impacts appartiennent aux <strong>mesures</strong>, pas à cette page :
          les modifier ici les modifie partout où ces mesures sont proposées.
          Rien n'est écrit avant « Enregistrer ».
        </div>
        <button
          type="button"
          className="builder-btn-secondary score-matrix-expand"
          onClick={onToggleFullscreen}
          title={fullscreen ? "Réduire (Échap)" : "Agrandir le tableau à tout l'écran"}
        >{fullscreen ? "⤡ Réduire" : "⤢ Plein écran"}</button>
      </div>

      <div className="score-matrix-scroll">
        <table className="score-matrix-table">
          <thead>
            <tr>
              <th className="score-matrix-corner">Score \ Mesure</th>
              {decisions.map((dec, idx) => (
                <th key={dec._id} className="score-matrix-colhead" title={dec.title || dec._id}>
                  <button
                    type="button"
                    className="score-matrix-colbtn"
                    onClick={() => onEditDecision?.(dec._id)}
                    title="Ouvrir cette mesure"
                  >
                    <span className="score-matrix-colindex">n°{idx + 1}</span>
                    <span className="score-matrix-coltitle">{dec.title || dec._id}</span>
                  </button>
                </th>
              ))}
            </tr>
          </thead>

          {groups.map((group) => (
            <tbody key={group.type} className={`score-matrix-group is-${group.type}`}>
              <tr className="score-matrix-grouprow">
                <th className="score-matrix-grouphead" colSpan={decisions.length + 1}>
                  {group.label}
                  <span className="score-matrix-groupcount">
                    {group.scoreIds.length} score{group.scoreIds.length > 1 ? "s" : ""}
                  </span>
                </th>
              </tr>
              {group.scoreIds.map((scoreId) => {
                const def = scoreDic?.[scoreId] || {};
                const shape = scoreShape(def);
                const read = shape === "composite" ? compositeTotal : simpleImpact;
                const total = shape === "profile"
                  ? null
                  : decisions.reduce((sum, dec) => sum + (read(dec, scoreId) ?? 0), 0);
                return (
                  <tr key={scoreId}>
                    <th className="score-matrix-rowhead" title={scoreId}>
                      <span className="score-matrix-rowinner">
                        {def.icon
                          ? <img src={def.icon} alt="" className="score-matrix-rowicon" />
                          : <span className="score-matrix-rowicon is-empty" aria-hidden="true" />}
                        <span className="score-matrix-rowname">{def.name || scoreId}</span>
                        {shape === "composite" && (
                          <span className="score-matrix-shape" title="Score composite : réparti en postes">⊞</span>
                        )}
                        {shape === "profile" && (
                          <span className="score-matrix-shape" title="Impact en profil, étalé sur les rounds">∿</span>
                        )}
                        <span className="score-matrix-rowtotal" title="Total si toutes les mesures sont prises">
                          {total === null ? "—" : total > 0 ? `+${total}` : total}
                        </span>
                      </span>
                    </th>
                    {decisions.map((dec) => (
                      <td key={`${dec._id}|${scoreId}`} className="score-matrix-cell">
                        {cell(dec, scoreId, shape)}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          ))}

          {rows.length === 0 && (
            <tbody>
              <tr>
                <td colSpan={decisions.length + 1} className="builder-empty">
                  Aucune mesure de cette page n'a d'impact sur un score.
                </td>
              </tr>
            </tbody>
          )}
        </table>
      </div>

      <label className="score-matrix-toggle">
        <input
          type="checkbox"
          checked={showAllScores}
          onChange={(e) => setShowAllScores(e.target.checked)}
        />
        Afficher tous les scores de l'atelier ({Object.keys(scoreDic || {}).length}), y compris ceux qu'aucune mesure ne touche
      </label>

      {composing && (
        <CompositeEditor
          decision={decisionsById[composing.decId]}
          scoreId={composing.scoreId}
          scoreDef={scoreDic?.[composing.scoreId]}
          onChange={(part, raw) => setCompositePart(composing.decId, composing.scoreId, part, raw)}
          onClear={() => { writeEntry(composing.decId, composing.scoreId, null); setComposing(null); }}
          onClose={() => setComposing(null)}
        />
      )}
    </div>
  );
}

/**
 * L'ÉDITEUR D'UN IMPACT COMPOSITE. Un score composite est un total réparti en
 * postes — l'empreinte eau d'un chantier par usage, par exemple. Une mesure ne
 * le déplace pas en bloc : elle agit sur certains postes.
 *
 * Les postes proposés sont ceux DU SCORE (`metadata.current`), pas ceux déjà
 * saisis : le moteur ignore silencieusement un poste qu'il ne connaît pas, donc
 * un poste inventé ici ne produirait aucun effet et aucune erreur — le pire des
 * deux mondes. La liste fermée rend ce cas impossible.
 */
function CompositeEditor({ decision, scoreId, scoreDef, onChange, onClear, onClose }) {
  const parts = partsOf(scoreDef);
  const current = decision?.score?.[scoreId]?.metadata || {};
  const reference = scoreDef?.metadata?.standard || {};
  const total = Object.values(current)
    .filter((v) => typeof v === "number")
    .reduce((sum, v) => sum + v, 0);

  return (
    <div className="builder-picker-overlay" onClick={onClose}>
      <div className="builder-picker" onClick={(e) => e.stopPropagation()}>
        <div className="builder-picker-header">
          <h3>{scoreDef?.name || scoreId} · {decision?.title || decision?._id}</h3>
          <button onClick={onClose}>✕</button>
        </div>

        <div className="composite-editor">
          <p className="step-editor-section-hint">
            Ce score est réparti en postes. Indiquez ce que cette mesure change
            sur chacun — laissez vide un poste qu'elle ne touche pas.
          </p>

          {parts.length === 0 && (
            <div className="builder-warning-inline">
              Ce score n'a aucun poste déclaré dans l'atelier ({"metadata.current"}).
              Rien ne peut y être imputé tant que ce n'est pas corrigé dans l'éditeur d'atelier.
            </div>
          )}

          {parts.map((part) => (
            <label key={part} className="composite-editor-row">
              <span className="composite-editor-part">{part}</span>
              {reference[part] !== undefined && (
                <span className="composite-editor-ref" title="Valeur de référence de ce poste dans l'atelier">
                  réf. {reference[part]}
                </span>
              )}
              <input
                className="builder-input small"
                type="number"
                value={current[part] ?? ""}
                placeholder="—"
                onChange={(e) => onChange(part, e.target.value)}
              />
            </label>
          ))}

          {parts.length > 0 && (
            <div className="composite-editor-total">
              Effet cumulé de cette mesure : <strong>{total > 0 ? `+${total}` : total}</strong>
            </div>
          )}
        </div>

        <div className="builder-picker-footer">
          <button className="builder-btn-danger" onClick={onClear}>Retirer cet impact</button>
          <button className="builder-btn-primary" onClick={onClose}>Terminer</button>
        </div>
      </div>
    </div>
  );
}
